import React, { useState } from 'react';
import Reveal from '../components/Reveal';
import usePageTitle from '../hooks/usePageTitle';
import { images, speaking, workshops, courses, publications } from '../mock';
import { Mic, Presentation, GraduationCap, BookOpen, ArrowUpRight, Calendar } from 'lucide-react';

const tabs = [
  { id: 'speaking', label: 'Speaking', icon: Mic },
  { id: 'workshops', label: 'Workshops & Consulting', icon: Presentation },
  { id: 'teaching', label: 'Academic Teaching', icon: GraduationCap },
  { id: 'publications', label: 'Publications', icon: BookOpen },
];

const EngagementCard = ({ item }) => (
  <div className="card-lift bg-white rounded-sm p-8 h-full border-l-2 border-[#43265E]">
    <div className="flex items-start justify-between gap-4 mb-3">
      <h3 className="font-display text-2xl text-[#241033] leading-snug">{item.title}</h3>
    </div>
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-4 text-sm text-[#43265E] font-medium">
      <span>{item.org}</span>
      {item.location && <span className="text-[#9A8AAF]">· {item.location}</span>}
    </div>
    <div className="flex flex-wrap gap-3 mb-4">
      {item.date && (
        <span className="inline-flex items-center gap-1.5 text-xs tracking-luxe uppercase text-[#6A6570] bg-[#F4F1EC] px-3 py-1 rounded-full">
          <Calendar className="w-3 h-3" strokeWidth={1.5} /> {item.date}
        </span>
      )}
      {item.role && (
        <span className="inline-flex items-center text-xs tracking-luxe uppercase text-[#43265E] bg-[#EDE8F2] px-3 py-1 rounded-full">
          {item.role}
        </span>
      )}
    </div>
    <p className="text-[#6A6570] font-light text-sm leading-relaxed">{item.focus}</p>
  </div>
);

const ThoughtLeadership = () => {
  usePageTitle('Thought Leadership');
  const [active, setActive] = useState('speaking');

  return (
    <main>
      {/* Hero */}
      <section className="relative pt-40 pb-24">
        <div className="absolute inset-0">
          <img src={images.thoughtLeadershipHero} alt="" className="w-full h-full object-cover object-left" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#241033] via-[#241033]/94 to-[#43265E]/72" />
        </div>
        <div className="relative max-w-[1280px] mx-auto px-6">
          <Reveal>
            <div className="eyebrow text-silver mb-5">Thought Leadership</div>
            <h1 className="font-display text-5xl md:text-6xl text-white font-medium leading-tight max-w-3xl mb-6">
              Speaking, workshops & written work at the intersection of people, systems & strategy.
            </h1>
            <p className="text-[#D9CEE6] font-light text-lg max-w-2xl">
              Making I/O psychology visible—translating research into leadership discernment, culture, and the deeply human experience of work.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Tabs */}
      <section className="bg-[#FBFAF8] sticky top-[68px] z-30 border-b border-[#E7E2EE]">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="flex flex-wrap gap-2 py-4">
            {tabs.map((t) => {
              const Icon = t.icon;
              const isActive = active === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActive(t.id)}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm tracking-luxe uppercase transition-all ${
                    isActive ? 'bg-[#43265E] text-white' : 'bg-transparent text-[#4A4453] hover:bg-[#EDE8F2]'
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-[#FBFAF8] min-h-[50vh]">
        <div className="max-w-[1280px] mx-auto px-6">
          {active === 'speaking' && (
            <div className="grid md:grid-cols-2 gap-6">
              {speaking.map((item, i) => (
                <Reveal key={item.title} delay={(i % 2) * 80}><EngagementCard item={item} /></Reveal>
              ))}
            </div>
          )}

          {active === 'workshops' && (
            <div className="grid md:grid-cols-2 gap-6">
              {workshops.map((item, i) => (
                <Reveal key={item.title} delay={(i % 2) * 80}><EngagementCard item={item} /></Reveal>
              ))}
            </div>
          )}

          {active === 'teaching' && (
            <div>
              <Reveal>
                <p className="font-display text-2xl text-[#43265E] mb-10 max-w-3xl">
                  Adjunct Professor at Florida International University—where teaching and practice stay deeply connected.
                </p>
              </Reveal>
              <div className="grid md:grid-cols-2 gap-6">
                {courses.map((c, i) => (
                  <Reveal key={c.title} delay={(i % 2) * 80}>
                    <div className="card-lift bg-white rounded-sm p-8 h-full border-l-2 border-[#43265E]">
                      <GraduationCap className="w-6 h-6 text-[#43265E] mb-4" strokeWidth={1.5} />
                      <h3 className="font-display text-2xl text-[#241033] mb-3">{c.title}</h3>
                      <p className="text-[#6A6570] font-light text-sm leading-relaxed">{c.focus}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          )}

          {active === 'publications' && (
            <div className="grid md:grid-cols-3 gap-6">
              {publications.map((p, i) => (
                <Reveal key={p.title} delay={i * 90}>
                  <div className="card-lift bg-white rounded-sm p-8 h-full flex flex-col">
                    <div className="w-12 h-12 rounded-full bg-silver flex items-center justify-center mb-6">
                      <BookOpen className="w-5 h-5 text-[#241033]" strokeWidth={1.5} />
                    </div>
                    <span className="eyebrow text-[#7E5C9E] mb-3">{p.type}</span>
                    <h3 className="font-display text-2xl text-[#241033] mb-3 leading-snug">{p.title}</h3>
                    <p className="text-[#6A6570] font-light text-sm leading-relaxed mb-5 flex-1">{p.focus}</p>
                    <span className="text-xs tracking-luxe uppercase text-[#43265E] bg-[#EDE8F2] px-3 py-1.5 rounded-full self-start">{p.status}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Feature textbook banner */}
      <section className="py-24 bg-[#241033] relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#43265E]/50 blur-3xl" />
        <div className="relative max-w-[1000px] mx-auto px-6 text-center">
          <div className="eyebrow text-[#7E5C9E] mb-6">Featured Publication</div>
          <h2 className="font-display text-4xl md:text-5xl text-white font-medium leading-tight mb-6">
            Women &amp; Leadership: <span className="italic text-silver">Identity, Power, &amp; Practice</span>
          </h2>
          <p className="text-[#B9A9CC] font-light text-lg max-w-2xl mx-auto">
            A textbook designed to make complex psychological and leadership concepts accessible, practical, and relevant—for students, leaders, and organizations alike.
          </p>
        </div>
      </section>
    </main>
  );
};

export default ThoughtLeadership;
